import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setCheckedKeys,
  setSignificantFiguresSettings,
  setUseMcDanielForecasts,
  setUseNoviForecasts,
  setUseWorkingForecasts,
  setUserForecastSettings
} from "store/features";
import {
  setActiveColorPalette,
  setActiveFacilityColorPalette,
  setIsDefaultSettingsLoaded
} from "store/features/userSettings/userSettingsSlice";
import { RootState } from "store/rootReducer";
import { useForecastStore } from "stores/forecast";
import { useMapStore } from "stores/map";

import useFeatureFlags from "hooks/useFeatureFlags";

import { setDisplayUnitHeader } from "api/displayUnitHeader";

import { useProjectListQuery } from "components/project/projects/queries";
import { useGetFavouriteColumns } from "components/user-settings/hooks";
import useRefreshPreferredColorsInStore from "components/user-settings/hooks/refreshPreferredColorsInStore";
import {
  DEFAULT_UNIT_SETTING,
  DEFAULT_ZOOM,
  DefaultForecastSource
} from "components/user-settings/models";

import { useUserDefaults, useUserSettingMutation, useUserSettings } from ".";
import {
  MCDANIEL_FOLDER_ID,
  SYSTEM_DEFAULT_FORECAST_FOLDER_ID,
  USER_ARPS
} from "../../../constants";
import { EntityKind } from "../../../models/entityKind";
import {
  DEFAULT_FORECAST_SOURCE_SETTING,
  getForecastSourceConfig
} from "../../user-settings/constants/userSetting.constants";
import { useEffectiveForecastFolderId } from "../../user-settings/hooks/useEffectiveForecastFolderId";
import { DEFAULT_USER_ABBREVIATIONS } from "../constants/userAbbreviations";
import { UserSettingsT, useUserContext } from "../context";
import {
  updateSettingFocus,
  updateSettingProject,
  updateSettings,
  updateUser
} from "../context/reducer";
import {
  updateForecastSource,
  updateSettingFacilityFocus,
  updateZoomToFilterSetting
} from "../context/reducer/UserReducer";
import { useUserSettingsDefaultsQuery } from "../queries";
import { IUserSettingsDefaults } from "../queries/useUserSettingsDefaults/userSettingsDefaultsModels";
import { toFocusTileProperties } from "./toFocusTileProperties";

/**
 * Picks the saved forecast-source record out of the settings response.
 *
 * Deliberately not `Object.values(group)[0]` like the other settings, for three reasons this
 * feature can't tolerate — it writes persisted state, so a failed read-back means a new row on
 * every page load:
 *  - an EMPTY object under the live key must fall through to the next candidate, where `??`
 *    alone would keep it and yield the seed with no id;
 *  - a record with no `forecastFolderId` is not usable and must not look like a saved value;
 *  - accounts can carry DUPLICATE rows (the shared-dev incident), and an arbitrary pick reads
 *    back a different one per load, so choose deterministically by id.
 */
function pickForecastSourceRecord(...groups: unknown[]) {
  let unusableRowCount = 0;
  let idlessRowCount = 0;

  for (const group of groups) {
    const allRows = Object.values(
      (group as Record<string, DefaultForecastSource>) ?? {}
    ).filter((row) => !!row && typeof row === "object");
    const rows = allRows.filter((row) => !!row.forecastFolderId);
    unusableRowCount += allRows.length - rows.length;

    if (rows.length) {
      idlessRowCount = rows.filter((row) => !row.id).length;
      if (idlessRowCount > 0) {
        // Reported here, before the return, because this is the only place that sees the raw
        // rows. A stored row with no id cannot be PUT against, so it is a state worth knowing
        // about even though the pick below no longer lets it masquerade as un-persisted.
        Sentry.captureMessage(
          `defaultForecastSource returned ${idlessRowCount} record(s) with no id — a PUT against them is not possible`,
          "warning"
        );
      }

      // A row with no id must LOSE the tie-break, not win it. Sorting on `id ?? ""` alone put
      // it first — "" precedes every real id — so an id-less row was preferred even when a
      // good one sat beside it, and the caller then treated the pick as un-persisted and
      // POSTed a duplicate. Rows carrying an id sort ahead; ids then order among themselves.
      return [...rows].sort((a, b) => {
        if (!a.id !== !b.id) return a.id ? -1 : 1;
        return String(a.id ?? "").localeCompare(String(b.id ?? ""));
      })[0];
    }
  }

  // Rows existed but none carried a forecastFolderId. Returning undefined here makes the caller
  // fall back to the seed, which looks like "no saved default" and qualifies the user — so the
  // switch POSTs a new record on every load with nothing to signal why. This is the only place
  // that still knows a malformed row was seen, so report it here.
  if (unusableRowCount > 0) {
    Sentry.captureMessage(
      `defaultForecastSource returned ${unusableRowCount} record(s) with no forecastFolderId — falling back to the default`,
      "warning"
    );
  }

  return undefined;
}

const useInitialUserContextLoader = () => {
  // hooks
  const [, dispatch] = useUserContext();
  const settings = useUserSettings();
  const defaults = useUserDefaults();
  const refreshPreferredColorsInStore = useRefreshPreferredColorsInStore();

  // dispatches
  const storeDispatch = useDispatch();
  const setMapState = useMapStore((s) => s.setMapState);
  const setForecastStoreCheckedForecastKeys = useForecastStore(
    (s) => s.setCheckedForecastKeys
  );
  const setForecastStoreUseMcDanielForecasts = useForecastStore(
    (s) => s.setUseMcDanielForecasts
  );
  const setForecastStoreUseWorkingForecasts = useForecastStore(
    (s) => s.setUseWorkingForecasts
  );
  const setForecastStoreUseNoviForecasts = useForecastStore((s) => s.setUseNoviForecasts);
  const setForecastStoreCheckedForecasts = useForecastStore((s) => s.setCheckedForecasts);
  const setForecastStoreCheckedGlobalForecasts = useForecastStore(
    (s) => s.setCheckedGlobalForecasts
  );
  const resetForecastStore = useForecastStore((s) => s.reset);

  // selectors
  const user = useSelector((state: RootState) => state.auth.user);
  const colorPalettes = useSelector(
    (state: RootState) => state.userSetting.colorPalettes
  );
  const { hasFeature } = useFeatureFlags();
  // "Base Midstream" is a hard prerequisite for all midstream/facility
  // functionality (Midstream Allocation Model provisioning guarantees
  // MAM-on implies Base-on). Read once here so every consumer of
  // `useUserSettings().midstreamSettings.enabled` (Home.tsx's showFacility,
  // ActivityBar's header clamp, ChartWrapper, DataTable, etc.) is correct
  // with zero changes to those files.
  const hasBaseMidstream = hasFeature("Base Midstream");

  // state
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);

  // Latches below exist so a settings refetch (including the one our own write triggers)
  // can't re-run logic that already ran or re-check a box the user just unchecked:
  // - hasResolvedSystemDefaultRef: the switch effect runs at most once per page lifetime.
  // - lastAppliedForecastFolderIdRef: the applier only re-applies when the resolved VALUE
  //   actually changes, not on every refetch that returns the same value.
  // - isSystemDefaultWriteRef: marks the next value change as "ours", not the user's, so
  //   the applier's pristine guard treats it the same as the initial load, not an override.
  const hasResolvedSystemDefaultRef = useRef(false);
  // Set from the settings READ (handleSettingsLoaded), not inferred from the stored value —
  // see there for why the value cannot answer this. Starts false so a switch that somehow runs
  // before the read errs toward not writing.
  const hasNoStoredForecastSourceRef = useRef(false);
  const lastAppliedForecastFolderIdRef = useRef<Nullable<string>>(null);
  // The raw saved value last applied, tracked separately from the effective one so an
  // availability-driven change (same saved value, different meaning) is distinguishable from a
  // deliberate user change and can be held to the pristine guard.
  const lastAppliedRawFolderIdRef = useRef<Nullable<string>>(null);
  // Holds the VALUE we wrote, not a boolean — see the applier for why.
  const isSystemDefaultWriteRef = useRef<Nullable<string>>(null);

  const { effectiveFolderId, isReady: isEffectiveFolderIdReady } =
    useEffectiveForecastFolderId(defaults?.forecastSourceSetting?.forecastFolderId);

  const { mutate: mutateDefaultForecastSource } =
    useUserSettingMutation<DefaultForecastSource>(
      DEFAULT_FORECAST_SOURCE_SETTING,
      (data) => {
        // Lets the applier see the value change and apply both sources, and lets a later
        // mid-session User Settings edit PUT against this record instead of POSTing a duplicate.
        updateForecastSource(dispatch, {
          id: data.id,
          forecastFolderId: data.forecastFolderId
        });
      },
      (error) => {
        // No toast, no state change — a failed write leaves the persisted value unchanged and the
        // user simply re-qualifies on the next full load (React Query v3 defaults to retry: 0).
        Sentry.withScope((scope) => {
          scope.setContext("defaultForecastSourceSwitch", {
            forecastFolderId: SYSTEM_DEFAULT_FORECAST_FOLDER_ID
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
        // A failed write produces no value change, so the applier never re-runs to clear this
        // marker — left set, it would wrongly attribute the user's next deliberate change to us.
        isSystemDefaultWriteRef.current = null;
      }
    );

  /**
   * Because we use this in the AppRouter, the queries in the hook will get called on the sign in page
   * before a user is signed in. It'll just create 401s until the user is signed in.
   * */
  const isAutoFetching = !!user;

  const handleSettingsLoaded = useCallback(
    (loadedUserSettings) => {
      if (!loadedUserSettings) return;

      // Typed narrowly for this one read only — IUserSettingsDefaults is missing other
      // properties, so typing the whole loadedUserSettings param would add unrelated errors.
      const typedSettings = loadedUserSettings as Partial<IUserSettingsDefaults>;

      const getSingleObjectSetting = (object, defaultObject = undefined) => {
        return Object.values(object ?? {})?.[0] ?? defaultObject;
      };

      // Hoisted out of newSettings so "the read found nothing" can be recorded as a fact,
      // rather than re-derived later from the value that gets stored. The System Default
      // switch needs to know whether the user has EVER set a source, and no property of the
      // resulting value can answer that: the seed is indistinguishable from a stored row
      // carrying the same value, so inferring it let a malformed row look un-persisted and
      // POST a duplicate on every load. This is the only point that knows the difference.
      // The unit the UI formats with and the header sends: from the response alone, defaulting
      // to hybrid as user-svc does for the claim. Not the context's current value, which after a
      // same-tab sign-out still holds the previous user's unit.
      const unitSetting = getSingleObjectSetting(
        loadedUserSettings.UnitSetting,
        DEFAULT_UNIT_SETTING
      );

      const storedForecastSourceRecord = pickForecastSourceRecord(
        loadedUserSettings.DefaultForecastSource,
        typedSettings.ForecastSource
      );
      hasNoStoredForecastSourceRef.current = !storedForecastSourceRecord;
      const newSettings = {
        ...settings,
        defaults: {
          mapSettings: getSingleObjectSetting(
            loadedUserSettings.MapCenter,
            settings.defaults.mapSettings
          ),
          focusTiles: getSingleObjectSetting(
            loadedUserSettings.FocusTile,
            settings.defaults.focusTiles
          ),
          facilityFocusTiles: getSingleObjectSetting(
            loadedUserSettings.FacilityFocusTile,
            settings.defaults.facilityFocusTiles
          ),
          project: getSingleObjectSetting(
            loadedUserSettings.Project,
            settings.defaults.project
          ),
          filter: getSingleObjectSetting(
            loadedUserSettings.Filter,
            settings.defaults.filter
          ),
          facilityFilter: getSingleObjectSetting(
            loadedUserSettings.FacilityFilter,
            settings.defaults.facilityFilter
          ),
          colorPalette: getSingleObjectSetting(
            loadedUserSettings.ColorPalette,
            settings.defaults.colorPalette
          ),
          facilityPalette: getSingleObjectSetting(
            loadedUserSettings.FacilityColorPalette,
            settings.defaults.facilityPalette
          ),
          typeWellDashboard: getSingleObjectSetting(
            loadedUserSettings.TypeWellDashboard,
            settings.defaults.typeWellDashboard
          ),
          binSettings: getSingleObjectSetting(
            loadedUserSettings.BinSettings,
            settings.defaults.binSettings
          ),
          geoSettings: getSingleObjectSetting(
            loadedUserSettings.GeoSettings,
            settings.defaults.geoSettings
          ),
          mapTooltipFields: getSingleObjectSetting(
            loadedUserSettings.MapTooltipField,
            settings.defaults.mapTooltipFields
          ),
          facilityMapTooltipFields: getSingleObjectSetting(
            loadedUserSettings.FacilityMapTooltipField,
            settings.defaults.facilityMapTooltipFields
          ),
          // The live response nests this setting under "DefaultForecastSource", not the
          // declared "ForecastSource" (that type is stale). pickForecastSourceRecord above
          // reads the live key first and keeps the declared one as tolerance only, so a
          // server-side rename can't silently break read-back and cause duplicate POSTs.
          forecastSourceSetting:
            storedForecastSourceRecord ?? settings.defaults.forecastSourceSetting,
          unitSetting,
          significantFiguresSetting: getSingleObjectSetting(
            loadedUserSettings.SignificantFiguresSetting,
            settings.defaults.significantFiguresSetting
          ),
          mapStyle: getSingleObjectSetting(
            loadedUserSettings.MapStyle,
            settings.defaults.mapStyle
          )
        },
        autoZoomSetting: getSingleObjectSetting(
          loadedUserSettings.AutoZoomSetting,
          settings.autoZoomSetting
        ),
        forecastExport: getSingleObjectSetting(
          loadedUserSettings.ExportForecastPreferences,
          settings.forecastExport
        ),
        userDocSettings: getSingleObjectSetting(
          loadedUserSettings.UserDocModel,
          settings.userDocSettings
        ),
        declineType: getSingleObjectSetting(
          loadedUserSettings.DeclineType,
          settings.declineType
        ),
        midstreamChartBackgroundColour: getSingleObjectSetting(
          loadedUserSettings.MidstreamChartBackgroundColour,
          settings.midstreamChartBackgroundColour
        ),
        userDashboardSettings: getSingleObjectSetting(
          loadedUserSettings.DashboardPreferences,
          settings.userDashboardSettings
        ),
        // EVA-10: force `enabled: false` (rather than dropping the record) when
        // "Base Midstream" is disabled, so the server-persisted preference is
        // never honored while the flag is off but is NOT destroyed — re-enabling
        // the flag restores whatever the user had set before.
        midstreamSettings: hasBaseMidstream
          ? getSingleObjectSetting(
              loadedUserSettings.MidstreamSettings,
              settings.midstreamSettings
            )
          : {
              ...getSingleObjectSetting(
                loadedUserSettings.MidstreamSettings,
                settings.midstreamSettings
              ),
              enabled: false
            },
        geoModelSettings: getSingleObjectSetting(
          loadedUserSettings.GeoModelSettings,
          settings.geoModelSettings
        ),
        screenshotPresets: loadedUserSettings.ScreenshotPreset,
        userAbbreviations: getSingleObjectSetting(loadedUserSettings.UserAbbreviations),
        // Keep all entries (Well + Facility) — do not collapse with getSingleObjectSetting.
        // EVA-882 FIX 2: dedupe by id (last-wins) so a pre-existing duplicate
        // server entry cannot break the entityKind lookup or produce two POST
        // calls for the same entityKind when two instances race before the first
        // onSuccess upserts the context.
        dataTableColumnOrder: Array.from(
          new Map(
            Object.values(loadedUserSettings.DataTableColumnOrderSetting ?? {}).map(
              (e) => [(e as { id: string }).id, e]
            )
          ).values()
        ) as UserSettingsT["dataTableColumnOrder"]
      } as UserSettingsT;

      newSettings.userAbbreviations = {
        id: newSettings.userAbbreviations?.id,
        abbreviations: [
          ...DEFAULT_USER_ABBREVIATIONS,
          ...(newSettings.userAbbreviations?.abbreviations ?? [])
        ]
      };
      // A unit change reloads the page, so this is also where a new unit takes effect.
      setDisplayUnitHeader(unitSetting?.unit);
      updateSettings(dispatch, newSettings);
      setSettingsLoaded(true);
    },
    [settings, hasBaseMidstream]
  );

  const userSettingsDefaultsQuery = useUserSettingsDefaultsQuery({
    onSuccess: handleSettingsLoaded,
    isAutoFetching: isAutoFetching
  });

  const handleFavouriteColumnsLoaded = (columns) => {
    updateSettingFocus(dispatch, {
      tiles: toFocusTileProperties(columns)
    });
  };
  const handleFacilityFavouriteColumnsLoaded = (columns) => {
    updateSettingFacilityFocus(dispatch, {
      tiles: toFocusTileProperties(columns)
    });
  };
  const { mutate: getFavouriteColumnsMutate } = useGetFavouriteColumns(
    EntityKind.Well,
    handleFavouriteColumnsLoaded
  );
  const { mutate: getFacilityFavouriteColumnsMutate } = useGetFavouriteColumns(
    EntityKind.Facility,
    handleFacilityFavouriteColumnsLoaded
  );
  // Whose unit the header carries. Impersonation switches user without a logout, so a different
  // user must not send the last one's unit while their own settings load; the SSO bridge
  // re-minting the same user's token is a new user object that keeps it.
  const displayUnitUserIdRef = useRef(user?.id);
  useEffect(() => {
    updateUser(dispatch, user);
    if (user) {
      setSettingsLoaded(false);
      const previousUserId = displayUnitUserIdRef.current;
      if (user.id !== previousUserId) {
        setDisplayUnitHeader(undefined);
        displayUnitUserIdRef.current = user.id;
      }
      if (previousUserId && user.id !== previousUserId) {
        // refetch() would join a fetch still in flight for the previous user, whose response
        // would then apply their settings, unit included, to this one.
        userSettingsDefaultsQuery
          .cancel()
          .then(() => userSettingsDefaultsQuery.refetch());
      } else {
        userSettingsDefaultsQuery.refetch();
      }
    }
  }, [user]);

  // Zoom to filter is session-only and must start off on every fresh sign-in.
  // Reset it here — the deepest seam every logout path flows through — rather
  // than in the sign-out button, so a silent session expiry (token-refresh
  // failure dispatches logout() with no reload) can't carry the toggle into
  // the next login. Guard on a logged-in -> logged-out transition so the reset
  // never fires on the initial mount, when user starts undefined before sign-in.
  const wasLoggedInRef = useRef(!!user);
  useEffect(() => {
    if (wasLoggedInRef.current && !user) {
      updateZoomToFilterSetting(dispatch, { isZoomToFilterEnabled: false });
      // Reset the latches too, so a user signing in on the same tab after this one logs out
      // does not inherit the previous user's latch state.
      hasResolvedSystemDefaultRef.current = false;
      hasNoStoredForecastSourceRef.current = false;
      lastAppliedForecastFolderIdRef.current = null;
      lastAppliedRawFolderIdRef.current = null;
      isSystemDefaultWriteRef.current = null;
      // ...and the forecast selection itself. The full store reset only runs from the sign-out
      // button, while the silent-expiry path this block exists for dispatches the bare logout.
      // Without this the next user boots with the previous user's checked keys, the store is
      // non-pristine, the applier stands down, and they inherit that selection and its payload.
      resetForecastStore();
    }
    wasLoggedInRef.current = !!user;
  }, [user]);

  // Handle the map default settings loading.
  useEffect(() => {
    if (defaults?.mapSettings) {
      const newMapState = {
        lng: defaults.mapSettings.longitude,
        lat: defaults.mapSettings.latitude,
        zoom: defaults.mapSettings.zoom ?? DEFAULT_ZOOM
      };
      // confirm map state match the user set default map settings
      if (settingsLoaded) {
        storeDispatch(setIsDefaultSettingsLoaded(true));
      }
      setMapState(newMapState);
    }
  }, [defaults?.mapSettings, settingsLoaded]);

  // Handle the color palette default settings loading.
  useEffect(() => {
    if (defaults?.colorPalette && colorPalettes) {
      const match = colorPalettes.find((p) => p.name === defaults.colorPalette.name);
      if (match) {
        storeDispatch(
          setActiveColorPalette({
            ...match,
            reverse: defaults.colorPalette.reverse
          })
        );
        refreshPreferredColorsInStore(match);
      }
    }
  }, [defaults?.colorPalette, colorPalettes]);

  useEffect(() => {
    if (defaults?.facilityPalette && colorPalettes) {
      const match = colorPalettes.find((p) => p.name === defaults.facilityPalette.name);
      if (match) {
        storeDispatch(
          setActiveFacilityColorPalette({
            ...match,
            reverse: defaults.facilityPalette.reverse
          })
        );
        refreshPreferredColorsInStore(match);
      }
    }
  }, [defaults?.facilityPalette, colorPalettes]);

  // Handle no default focus tiles, so request the system default favourites.
  useEffect(() => {
    if (!settingsLoaded) return;

    if (!defaults?.focusTiles) {
      getFavouriteColumnsMutate();
    }
  }, [defaults?.focusTiles, defaults?.facilityFocusTiles, settingsLoaded]);

  useEffect(() => {
    if (!settingsLoaded) return;
    if (!defaults?.facilityFocusTiles) {
      getFacilityFavouriteColumnsMutate();
    }
  }, [defaults?.facilityFocusTiles, settingsLoaded]);

  // Handle no default project.
  const projectListQuery = useProjectListQuery({ isAutoRefetching: isAutoFetching });
  const [hasLoadedDefaultProject, setHasLoadedDefaultProject] = useState<boolean>(false);

  useEffect(() => {
    const defaultProject =
      Object.values(userSettingsDefaultsQuery?.data?.Project ?? {})?.[0] ?? undefined;

    if (
      !projectListQuery.isSuccess ||
      !userSettingsDefaultsQuery.isSuccess ||
      hasLoadedDefaultProject ||
      defaultProject
    ) {
      if (projectListQuery.isSuccess && defaultProject) {
        // ensure project default from user settings is the current project
        const matchedDefaultProjectId = projectListQuery.data?.find((project) => {
          return project.projectId === defaultProject.projectId;
        })?.projectId;
        if (matchedDefaultProjectId) {
          updateSettingProject(dispatch, {
            id: defaultProject?.id,
            projectId: matchedDefaultProjectId
          });
        }
      }
      return;
    }

    const nextProjectId = projectListQuery.data[0].projectId;

    updateSettingProject(dispatch, {
      projectId: nextProjectId
    });

    setHasLoadedDefaultProject(true);
  }, [
    hasLoadedDefaultProject,
    isAutoFetching,
    projectListQuery.isSuccess,
    settingsLoaded,
    userSettingsDefaultsQuery?.data,
    userSettingsDefaultsQuery?.isSuccess
  ]);

  // Applies the user's saved default forecast source to the live forecast selection. This is
  // the SOLE writer of the forecast panel/payload for the default — the switch effect below
  // only ever changes the setting's value, never the selection itself.
  useEffect(() => {
    if (!settingsLoaded) return;

    const forecastFolderId = defaults?.forecastSourceSetting?.forecastFolderId;
    if (!forecastFolderId) return;

    // Shared with useClearForecasts so both writers agree on what a persisted value means.
    // isReady is false while a Novi-dependent value is still waiting on the availability
    // query — applying early would check Novi and then uncheck it (the US7 flicker).
    if (!isEffectiveFolderIdReady) return;

    const config = getForecastSourceConfig(effectiveFolderId);

    // Latched on the VALUE, not object identity, so a refetch returning the same choice
    // (including the refetch our own write triggers) can't re-check a box the user just
    // unchecked, while a genuine mid-session change still takes effect.
    if (effectiveFolderId === lastAppliedForecastFolderIdRef.current) {
      // Clear the own-write marker even on this early return, or a write whose effect never
      // reaches the apply path below leaves it set and the user's NEXT deliberate change gets
      // misattributed to us and silently swallowed.
      isSystemDefaultWriteRef.current = null;
      lastAppliedRawFolderIdRef.current = forecastFolderId;
      return;
    }

    // getState() reads the live selection without adding useForecastStore as a dependency.
    // isPristine is true only if the store still holds its untouched default (McDaniel-only) —
    // if something else (a View, an EVA-63 snapshot) already set a different selection, we
    // must not overwrite it below.
    const forecastState = useForecastStore.getState();
    const isPristine =
      !forecastState.useNoviForecasts &&
      forecastState.checkedForecastKeys.length === 1 &&
      forecastState.checkedForecastKeys[0] === MCDANIEL_FOLDER_ID;

    // Value-scoped, not boolean: only the exact value we wrote counts as our own write, so a
    // marker left over from a write whose effect never landed can't mask a later user change.
    const isOwnWrite = isSystemDefaultWriteRef.current === effectiveFolderId;
    const isFirstApplication = lastAppliedForecastFolderIdRef.current === null;
    // An availability flip changes effectiveFolderId with NO change to the saved value. That is
    // not a user action, so it must face the pristine guard too — otherwise one transient
    // data-source failure silently rewrites the user's live selection mid-session.
    const rawValueChanged = forecastFolderId !== lastAppliedRawFolderIdRef.current;

    if ((isFirstApplication || isOwnWrite || !rawValueChanged) && !isPristine) {
      // Something more authoritative already won — stand down, but still latch.
      lastAppliedForecastFolderIdRef.current = effectiveFolderId;
      lastAppliedRawFolderIdRef.current = forecastFolderId;
      isSystemDefaultWriteRef.current = null;
      return;
    }
    isSystemDefaultWriteRef.current = null;
    lastAppliedForecastFolderIdRef.current = effectiveFolderId;
    lastAppliedRawFolderIdRef.current = forecastFolderId;

    setForecastStoreCheckedForecastKeys(config.checkedKeys);
    setForecastStoreUseMcDanielForecasts(config.useMcDaniel);
    setForecastStoreUseWorkingForecasts(config.useWorking);
    setForecastStoreUseNoviForecasts(config.useNovi);
    // Clear the per-forecast selections alongside the special-folder keys, as clear-forecasts
    // does. The chart query dependencies read these, so leaving them behind shows folders
    // unchecked in the tree while charts keep requesting them.
    setForecastStoreCheckedForecasts([]);
    setForecastStoreCheckedGlobalForecasts([]);

    storeDispatch(
      setUserForecastSettings({
        forecastSourceFolder: null,
        excludeMcDanielForecasts: config.excludeMcDaniel,
        reserveCategory: null,
        excludeNoviForecasts: config.excludeNovi,
        specialForecastFolders: config.specialFolderNames
      })
    );

    // Legacy Redux ARPS flags — dead state (the live readers are the Zustand writes above),
    // but kept untouched since it may still have an untraced consumer elsewhere.
    storeDispatch(setCheckedKeys({ type: USER_ARPS, checkedKeys: config.checkedKeys }));
    storeDispatch(setUseMcDanielForecasts(config.useMcDaniel));
    storeDispatch(setUseWorkingForecasts(config.useWorking));
    storeDispatch(setUseNoviForecasts(config.useNovi));
  }, [
    defaults?.forecastSourceSetting,
    settingsLoaded,
    effectiveFolderId,
    isEffectiveFolderIdReady
  ]);

  // Switches a qualifying user onto "System Default" once per page lifetime. Only changes the
  // setting's VALUE in user context — the applier effect above is what writes the selection.
  //
  // Deliberately NOT gated on Novi availability. "System Default" means "every forecast source
  // this org can actually use", and useEffectiveForecastFolderId is what narrows it at read
  // time — a non-Novi org holding "-4" resolves to McDaniel-only with excludeNovi set, so it
  // never asks for a source it has no credentials for. Gating the write on hasNoviAccess left
  // those orgs showing a literal "EVA Research" seed that no one had chosen and that would not
  // widen if the org later gained Novi (EVA-180).
  //
  // Dropping the availability check also removes this effect's dependency on an async answer.
  // It used to run on the first isResolved tick and burn the one-shot ref before checking
  // access — and isResolved is true even when getAccessibleDataSources soft-fails ({ ok: false }
  // rather than rejecting), so one blip spent the session's only attempt and left the user on
  // the seed until reload.
  useEffect(() => {
    if (!settingsLoaded || hasResolvedSystemDefaultRef.current) {
      return;
    }
    hasResolvedSystemDefaultRef.current = true;

    const forecastSourceSetting = defaults?.forecastSourceSetting;
    const forecastFolderId = forecastSourceSetting?.forecastFolderId;

    // A record that resolved but carries no forecastFolderId must NOT look like "no saved
    // default" — that would qualify the user, POST a new row because there is no id to PUT
    // against, and repeat on every load.
    //
    // The settings-load path can no longer reach this: pickForecastSourceRecord filters such
    // rows out and reports them itself. What remains is the mutation onSuccess path, where a
    // write response missing forecastFolderId lands straight in context.
    if (forecastSourceSetting && !forecastFolderId) {
      Sentry.captureMessage(
        "defaultForecastSource resolved without a forecastFolderId — skipping the System Default switch",
        "warning"
      );
      return;
    }

    // "Has not manually set it" means NO PERSISTED ROW, and only the settings READ can say
    // that — see hasNoStoredForecastSourceRef where it is set. Nothing about the resulting
    // value answers it: EVA-2 tested `folderId === MCDANIEL_FOLDER_ID`, which swept up both
    // unset users and users who deliberately picked EVA Research (nothing in the app has ever
    // persisted "-1", so every stored "-1" is a user's own pick). Testing "no id AND the value
    // is still the seed" then failed the other way, and circularly: the seed value IS what
    // this effect writes, so a stored row carrying "-4" with no id satisfied both halves,
    // qualified, and POSTed a fresh row on every single load — precisely the unbounded
    // duplication the test existed to stop.
    //
    // The write is what makes the seed durable. Skipping it would leave the user re-seeded on
    // every load, so a later change to the seed would silently move them.
    const qualifiesForSystemDefault = hasNoStoredForecastSourceRef.current;
    if (!qualifiesForSystemDefault) return;

    isSystemDefaultWriteRef.current = SYSTEM_DEFAULT_FORECAST_FOLDER_ID;
    mutateDefaultForecastSource({
      id: defaults?.forecastSourceSetting?.id,
      forecastFolderId: SYSTEM_DEFAULT_FORECAST_FOLDER_ID
    });
  }, [settingsLoaded, defaults?.forecastSourceSetting]);

  // Handle syncing significant figures from backend to Redux
  useEffect(() => {
    if (!settingsLoaded) return;

    if (defaults?.significantFiguresSetting) {
      storeDispatch(
        setSignificantFiguresSettings({
          defaultSignificantFigures:
            defaults.significantFiguresSetting.defaultSignificantFigures,
          useStrictRounding: defaults.significantFiguresSetting.useStrictRounding
        })
      );
    }
  }, [defaults?.significantFiguresSetting, settingsLoaded]);
};

export default useInitialUserContextLoader;
